import { loadConnectAndInitialize } from "@stripe/connect-js";
import { ConnectComponentsProvider } from "@stripe/react-connect-js";
import { useSwitcherClientCore } from "hooks/useSwitcherClient";
import { PropsWithChildren, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "store/reducers";

export interface StripeConnectComponentWrapperProps extends PropsWithChildren {
    /** Called when stripe session is successfully fetched from the API. */
    onSuccess?: () => void;
    /** Loading view to be displayed while the stripe session is being fetched. */
    loadingView?: React.ReactElement;
}

/**
 * Wrapper for the Stripe Connect components. This component loads the Stripe Connect instance and provides it to the children.
 * It also handles the loading state and the success callback.
 */
export const StripeConnectComponentWrapper = ({
    children,
    loadingView,
    onSuccess = () => {}
}: StripeConnectComponentWrapperProps) => {
    const {
        userInfo: {
            User: {
                Details: { Id: UserId }
            }
        }
    } = useSelector((state: RootState) => state.user);

    const { dispatchApiRequest: getAccountSession, loading: sessionLoading } =
        useSwitcherClientCore(
            (client) => client.stripeConnect_GetAccountSession,
            {
                requestImmediately: false,
                args: [UserId],
                onSuccess
            }
        );

    const [stripeConnectInstance] = useState(() => {
        return loadConnectAndInitialize({
            publishableKey: import.meta.env.VITE_STRIPE_API_KEY,
            fetchClientSecret: async () =>
                (await getAccountSession())?.Details?.ClientSecret
        });
    });

    if (sessionLoading) return loadingView ?? <></>;

    return (
        <ConnectComponentsProvider connectInstance={stripeConnectInstance}>
            {children}
        </ConnectComponentsProvider>
    );
};
